/**
 * Shared marketingContacts queries.
 *
 * Always select every marketingContacts hit field (the full MODEL_FIELDS.marketingContacts
 * list, inlined below) — hand-picked hit fields caused TRK-5487 (milestone dates in the
 * drawer via contact(), empty in the modal table).
 *
 * Typed graphql() documents require a static selection set, so the field list is
 * written out instead of interpolated.
 */
import { graphql } from 'app/generated/gql'

/** Compact args used by New Patients / Broken Appointments modals. */
export const MARKETING_CONTACTS_MODAL_QUERY = graphql(`
  query MarketingContactsModal(
    $filterBy: String!
    $fromDate: String!
    $toDate: String!
    $locationIds: [String!]
    $channel: String
    $pagination: PaginationInput
  ) {
    marketingContacts(
      filterBy: $filterBy
      fromDate: $fromDate
      toDate: $toDate
      locationIds: $locationIds
      channel: $channel
      pagination: $pagination
    ) {
      hits {
        version
        createdDate
        updatedDate
        id
        patientIds
        patientName
        orgId
        workspaceId
        locationId
        name
        preferredName
        homePhone
        cellPhone
        workPhone
        otherPhone
        email
        birthDate
        addressLine1
        addressLine2
        lifecycleStage
        patientStatus
        leadStatus
        isQualified
        appointmentStatus
        nearestAppointmentDate
        city
        state
        zip
        becameLeadDate
        becamePatientDate
        patientCreatedDate
        firstAppointmentMadeDate
        firstAppointmentDate
        lastAppointmentDate
        firstTransactionDate
        lastTransactionDate
        lastHygieneDate
        hygieneDueDate
        isPerio
        tags
        matchTypes
        collectedLtv
        productionLtv
        scheduledProduction
        unbookedTreatmentValue
        lastTreatmentPlanDate
        fvv
        adClickDate
        patientReferral
        lastActivityDate
        lastCommDate
        lastCommActivityType
        lastCommActivitySubType
        utmSource
        utmMedium
        utmCampaign
        utmKeywords
        utmContent
        platform
        trackableCampaignId
        leadOwner {
          id
          name
          aliases
          userId
        }
        channel
        smsMarketingConsent
        firstContactReason
        firstActivityType
        activityTypes
        convertedBy {
          id
          name
          aliases
          userId
        }
        firstActivityDate
        becameInactiveDate
        closedLostReason
        preferredCommunicationMethod
        preferredContact
        archived
        pipelines {
          id
          stage
          key
        }
        neededAction
        reportedAttribution
        activityCount
        needsActionDate
        firstName
        lastName
      }
      total
    }
  }
`)

/** Full filter surface used by MarketingContactTable. */
export const MARKETING_CONTACTS_TABLE_QUERY = graphql(`
  query MarketingContactsTable(
    $filterBy: String!
    $fromDate: String!
    $toDate: String!
    $pagination: PaginationInput
    $sort: SortInput
    $locationIds: [String!]
    $channel: String
    $rawChannel: String
    $platform: String
    $leadOwnerId: String
    $leadOwnerName: String
    $schedulingActivityType: String
    $firstActivityType: String
    $campaign: String
    $utmSource: String
    $utmMedium: String
    $utmCampaign: String
    $trackableCampaignId: String
    $reportedAttribution: String
    $showDisqualified: Boolean
    $hideNonPatients: Boolean
    $hideMultiLocationPatients: Boolean
    $search: String
    $filters: String
  ) {
    marketingContacts(
      filterBy: $filterBy
      fromDate: $fromDate
      toDate: $toDate
      pagination: $pagination
      sort: $sort
      locationIds: $locationIds
      channel: $channel
      rawChannel: $rawChannel
      platform: $platform
      leadOwnerId: $leadOwnerId
      leadOwnerName: $leadOwnerName
      schedulingActivityType: $schedulingActivityType
      firstActivityType: $firstActivityType
      campaign: $campaign
      utmSource: $utmSource
      utmMedium: $utmMedium
      utmCampaign: $utmCampaign
      trackableCampaignId: $trackableCampaignId
      reportedAttribution: $reportedAttribution
      showDisqualified: $showDisqualified
      hideNonPatients: $hideNonPatients
      hideMultiLocationPatients: $hideMultiLocationPatients
      search: $search
      filters: $filters
    ) {
      hits {
        version
        createdDate
        updatedDate
        id
        patientIds
        patientName
        orgId
        workspaceId
        locationId
        name
        preferredName
        homePhone
        cellPhone
        workPhone
        otherPhone
        email
        birthDate
        addressLine1
        addressLine2
        lifecycleStage
        patientStatus
        leadStatus
        isQualified
        appointmentStatus
        nearestAppointmentDate
        city
        state
        zip
        becameLeadDate
        becamePatientDate
        patientCreatedDate
        firstAppointmentMadeDate
        firstAppointmentDate
        lastAppointmentDate
        firstTransactionDate
        lastTransactionDate
        lastHygieneDate
        hygieneDueDate
        isPerio
        tags
        matchTypes
        collectedLtv
        productionLtv
        scheduledProduction
        unbookedTreatmentValue
        lastTreatmentPlanDate
        fvv
        adClickDate
        patientReferral
        lastActivityDate
        lastCommDate
        lastCommActivityType
        lastCommActivitySubType
        utmSource
        utmMedium
        utmCampaign
        utmKeywords
        utmContent
        platform
        trackableCampaignId
        leadOwner {
          id
          name
          aliases
          userId
        }
        channel
        smsMarketingConsent
        firstContactReason
        firstActivityType
        activityTypes
        convertedBy {
          id
          name
          aliases
          userId
        }
        firstActivityDate
        becameInactiveDate
        closedLostReason
        preferredCommunicationMethod
        preferredContact
        archived
        pipelines {
          id
          stage
          key
        }
        neededAction
        reportedAttribution
        activityCount
        needsActionDate
        firstName
        lastName
      }
      total
      page
    }
  }
`)
